/**
 * Notification Realtime Service
 *
 * Servizio singleton per gestire la connessione realtime Supabase
 * alle notifiche. Fornisce:
 * - Sottoscrizione efficiente con retry automatico
 * - Aggiornamento automatico dello store
 * - Monitoring e metriche
 */

import { supabase } from '../supabase';
import { useNotificationStore } from '../stores/notificationStore';
import type { Notification } from '../types';

// Tipi di eventi emessi dal service
export type NotificationRealtimeEvent =
  | 'notification-realtime-status'
  | 'notification-received';

export interface RealtimeStatusEvent {
  status: 'connected' | 'reconnecting' | 'error' | 'disconnected';
  error?: string;
  attempt?: number;
}

class NotificationRealtimeService {
  private channel: any = null;
  private isInitialized = false;
  private retryCount = 0;
  private maxRetries = 5;
  private retryTimeout: NodeJS.Timeout | null = null;
  private reconnecting = false;
  private userId: string | null = null;
  private organizationId: string | null = null;

  // Metriche
  private metrics = {
    notificationsReceived: 0,
    errors: 0,
    lastNotificationAt: null as Date | null,
    connectedAt: null as Date | null,
  };

  // Pause/Resume mechanism
  private paused = false;
  private bufferedEvents: any[] = [];

  /**
   * Inizializza il servizio e sottoscrive alle notifiche
   */
  async init(userId: string, organizationId: string) {
    // Se già inizializzato con lo stesso user, skip
    if (this.isInitialized && this.userId === userId) {
      return;
    }

    // Se userId è cambiato, disconnetti e riconnetti
    if (this.isInitialized && this.userId !== userId) {
      await this.disconnect();
    }

    // Verifica autenticazione
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return;
    }

    this.userId = userId;
    this.organizationId = organizationId;
    this.isInitialized = true;

    await this.connect();
  }

  /**
   * Crea e configura il canale Supabase
   */
  private async connect() {
    try {
      // Cleanup canale precedente se esiste
      if (this.channel) {
        await supabase.removeChannel(this.channel);
        this.channel = null;
      }

      if (!this.userId) {
        console.warn('[NotificationRealtimeService] No userId, skipping connection');
        return;
      }

      // Crea nuovo canale con ID univoco
      const channelId = `notifications_realtime_${Date.now()}`;
      this.channel = supabase.channel(channelId);

      if (!this.channel) {
        throw new Error('Impossibile creare canale Supabase');
      }

      // Configura sottoscrizione agli INSERT della tabella notifications
      this.channel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${this.userId}`
          },
          (payload: any) => this.handleInsert(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${this.userId}`
          },
          (payload: any) => this.handleUpdate(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${this.userId}`
          },
          (payload: any) => this.handleDelete(payload)
        )
        .subscribe((status: string, err?: Error) => {
          this.handleSubscriptionStatus(status, err);
        });

    } catch (error) {
      console.error('[NotificationRealtimeService] Errore connessione:', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Handler per le nuove notifiche (INSERT)
   */
  private handleInsert(payload: any) {
    console.log('[NotificationRealtimeService] 📥 INSERT received:', payload.new?.event_type, payload.new?.priority);
    // Se in pausa, bufferizza l'evento
    if (this.paused) {
      this.bufferedEvents.push({ type: 'INSERT', payload });
      return;
    }

    const newNotification = payload.new as Notification;

    // Aggiorna metriche
    this.metrics.notificationsReceived++;
    this.metrics.lastNotificationAt = new Date();

    // Aggiorna lo store
    const store = useNotificationStore.getState();

    // Determina se è una notifica WhatsApp o standard
    if (newNotification.whatsapp_chat_id) {
      // Notifica WhatsApp
      const currentWhatsAppCount = typeof store.whatsappUnreadCount === 'number' ? store.whatsappUnreadCount : 0;
      useNotificationStore.setState({
        whatsappNotifications: [newNotification, ...store.whatsappNotifications],
        whatsappUnreadCount: currentWhatsAppCount + 1
      });
      // Nota: rimosso emit 'whatsapp-notification-update' — il badge WhatsApp ora deriva
      // direttamente da useWhatsAppChatStore.chats[].unreadCount in Layout.
    } else {
      // Notifica standard
      const currentCount = typeof store.unreadCount === 'number' ? store.unreadCount : 0;
      useNotificationStore.setState({
        notifications: [newNotification, ...store.notifications],
        unreadCount: currentCount + 1
      });
    }

    // Emetti evento generico per UI
    this.emitEvent('notification-received', { notification: newNotification });

    // Mostra notifica browser se permesso
    this.showBrowserNotification(newNotification);
  }

  /**
   * Handler per aggiornamenti notifiche (UPDATE)
   */
  private handleUpdate(payload: any) {
    if (this.paused) {
      this.bufferedEvents.push({ type: 'UPDATE', payload });
      return;
    }

    const updatedNotification = payload.new as Notification;
    const store = useNotificationStore.getState();

    if (updatedNotification.whatsapp_chat_id) {
      // Aggiorna notifica WhatsApp
      const whatsappNotifications = store.whatsappNotifications.map(n =>
        n.id === updatedNotification.id ? updatedNotification : n
      );
      const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;
      useNotificationStore.setState({ whatsappNotifications, whatsappUnreadCount });
    } else {
      // Aggiorna notifica standard
      const notifications = store.notifications.map(n =>
        n.id === updatedNotification.id ? updatedNotification : n
      );
      const unreadCount = notifications.filter(n => !n.read).length;
      useNotificationStore.setState({ notifications, unreadCount });
    }
  }

  /**
   * Handler per eliminazioni notifiche (DELETE)
   */
  private handleDelete(payload: any) {
    if (this.paused) {
      this.bufferedEvents.push({ type: 'DELETE', payload });
      return;
    }

    const deletedNotification = payload.old as Notification;
    const store = useNotificationStore.getState();

    if (deletedNotification.whatsapp_chat_id) {
      const whatsappNotifications = store.whatsappNotifications.filter(n => n.id !== deletedNotification.id);
      const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;
      useNotificationStore.setState({ whatsappNotifications, whatsappUnreadCount });
    } else {
      const notifications = store.notifications.filter(n => n.id !== deletedNotification.id);
      const unreadCount = notifications.filter(n => !n.read).length;
      useNotificationStore.setState({ notifications, unreadCount });
    }
  }

  /**
   * Mostra notifica browser nativa (se permesso)
   */
  private async showBrowserNotification(notification: Notification) {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (document.hasFocus()) return; // Non mostrare se la pagina è in focus

    try {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/icon-192.png',
        tag: notification.id, // Evita duplicati
        data: { url: notification.link }
      });
    } catch (error) {
      // Ignora errori (es. Service Worker non registrato)
    }
  }

  /**
   * Gestisce gli stati della sottoscrizione
   */
  private handleSubscriptionStatus(status: string, err?: Error) {
    switch (status) {
      case 'SUBSCRIBED':
        console.log('[NotificationRealtimeService] ✅ SUBSCRIBED - listening for notifications');
        this.retryCount = 0;
        this.reconnecting = false;
        this.metrics.connectedAt = new Date();
        this.emitEvent('notification-realtime-status', { status: 'connected' });

        // Aggiorna lo store
        useNotificationStore.setState({ realtimeConnected: true });
        break;

      case 'CHANNEL_ERROR':
        console.error('[NotificationRealtimeService] Errore canale:', err?.message);
        this.metrics.errors++;
        this.handleConnectionError(err);
        break;

      case 'TIMED_OUT':
        console.error('[NotificationRealtimeService] Timeout');
        this.handleConnectionError(new Error('Timeout sottoscrizione'));
        break;

      case 'CLOSED':
        console.warn('[NotificationRealtimeService] Connection closed');
        useNotificationStore.setState({ realtimeConnected: false });
        if (this.isInitialized && !this.reconnecting) {
          // Riconnetti automaticamente se chiuso inaspettatamente
          this.scheduleReconnect();
        }
        break;
    }
  }

  /**
   * Gestisce errori di connessione con retry exponential backoff
   */
  private handleConnectionError(error: any) {
    useNotificationStore.setState({ realtimeConnected: false });

    if (this.retryCount >= this.maxRetries) {
      console.error('[NotificationRealtimeService] Max tentativi raggiunto');
      this.emitEvent('notification-realtime-status', {
        status: 'error',
        error: 'Impossibile connettersi'
      });
      return;
    }

    this.scheduleReconnect();
  }

  /**
   * Pianifica riconnessione con exponential backoff
   */
  private scheduleReconnect() {
    if (this.reconnecting || this.retryTimeout) {
      return; // Già in fase di riconnessione
    }

    this.reconnecting = true;
    this.retryCount++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 16000);

    this.emitEvent('notification-realtime-status', {
      status: 'reconnecting',
      attempt: this.retryCount
    });

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.connect();
    }, delay);
  }

  /**
   * Emette un evento custom
   */
  private emitEvent(eventName: string, detail: any) {
    try {
      const event = new CustomEvent(eventName, { detail });
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`[NotificationRealtimeService] Errore emissione evento ${eventName}:`, error);
    }
  }

  /**
   * Disconnette il servizio
   */
  async disconnect() {
    this.isInitialized = false;
    this.reconnecting = false;
    this.userId = null;
    this.organizationId = null;

    // Cancella retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Rimuovi canale
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }

    useNotificationStore.setState({ realtimeConnected: false });
    this.emitEvent('notification-realtime-status', { status: 'disconnected' });
  }

  /**
   * Ottieni metriche del servizio
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Forza riconnessione
   */
  async reconnect() {
    const userId = this.userId;
    const orgId = this.organizationId;
    await this.disconnect();
    if (userId && orgId) {
      await this.init(userId, orgId);
    }
  }

  /**
   * Pausa gli aggiornamenti realtime
   * Gli eventi vengono bufferizzati e applicati al resume
   */
  pause() {
    this.paused = true;
  }

  /**
   * Riprendi gli aggiornamenti realtime
   * Applica tutti gli eventi bufferizzati durante la pausa
   */
  resume() {
    this.paused = false;

    // Applica eventi bufferizzati
    if (this.bufferedEvents.length > 0) {
      this.bufferedEvents.forEach(({ type, payload }) => {
        switch (type) {
          case 'INSERT':
            this.handleInsert(payload);
            break;
          case 'UPDATE':
            this.handleUpdate(payload);
            break;
          case 'DELETE':
            this.handleDelete(payload);
            break;
        }
      });
      this.bufferedEvents = [];
    }
  }

  /**
   * Controlla se gli aggiornamenti sono in pausa
   */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Svuota il buffer senza applicare gli eventi
   */
  clearBuffer() {
    this.bufferedEvents = [];
  }

  /**
   * Controlla se il servizio è connesso
   */
  isConnected(): boolean {
    return this.isInitialized && !this.reconnecting;
  }
}

// Export singleton instance
export const notificationRealtimeService = new NotificationRealtimeService();
